import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { useSignVaultDocumentPartsMutation } from "#graphql/supergraphHooks";
import {
  type SignVaultDocumentPartsInput,
  type SignVaultDocumentPartsMutation,
  type SignVaultDocumentPartsMutationVariables,
} from "#graphql/supergraphTypes";

export type VaultSignedPart = SignVaultDocumentPartsMutation["signVaultDocumentParts"][number];

export interface UseSignVaultDocumentPartsReturn {
  signVaultDocumentParts: (input: SignVaultDocumentPartsInput) => Promise<VaultSignedPart[]>;
  loading: boolean;
  error: ReturnType<typeof useSignVaultDocumentPartsMutation>[1]["error"];
}

/**
 * DV-221 — step 2 of the MULTIPART upload path: mint one presigned PUT URL per
 * part of a pending version. The client then PUTs each slice of the file
 * directly to S3, keeping each part's ETag for `finalizeVaultDocument`.
 *
 * Usually driven via {@link useUploadVaultDocument}.
 */
export const useSignVaultDocumentParts = (
  options?: MutationHookOptions<
    SignVaultDocumentPartsMutation,
    SignVaultDocumentPartsMutationVariables
  >,
): UseSignVaultDocumentPartsReturn => {
  const [mutate, { loading, error }] = useSignVaultDocumentPartsMutation(options);

  const signVaultDocumentParts = useCallback(
    async (input: SignVaultDocumentPartsInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.signVaultDocumentParts ?? [];
    },
    [mutate],
  );

  return { signVaultDocumentParts, loading, error };
};
